/**
 * Intelligence Prism - Value Provenance Visualization
 *
 * Visualizes the journey from raw station findings through Bridge AI synthesis
 * to Pistoia-validated ROI projections. Shows the "white light → prism → spectrum"
 * transformation that proves attribution from evidence to value.
 *
 * Design: "The Prism" - Stations (chaos) → Bridge AI (synthesis) → Value Drivers (spectrum)
 *
 * Sub-modules extracted to ./intelligence-prism/ (DC-1368):
 * - types.ts, constants.ts, helpers.ts, transformPrismData.ts
 *
 * @version 1.1.0
 * @date 2026-02-14
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { useApiClient } from '../hooks/useApiClient';
import { useJourneyId } from '../contexts/JourneyContext';
import '../styles/intelligence-prism.css';

// Extracted modules (DC-1368)
import type { StationId, PrismData, IntelligencePrismProps } from './intelligence-prism/types';
import { DRIVER_COLORS, SEVERITY_COLORS } from './intelligence-prism/constants';
import { getSigmoidPath, isGovernanceDriver } from './intelligence-prism/helpers';
import { transformToPrismData } from './intelligence-prism/transformPrismData';

// Re-export public types for backward compatibility
export type { StationId, ValueDriver, BridgeRecommendation, ValueCalculationDetail, ActionPlanTask, PrismData } from './intelligence-prism/types';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const IntelligencePrism: React.FC<IntelligencePrismProps> = ({
  journeyId: journeyIdProp,
  onStationClick,
  onDriverClick,
  onDataLoaded,
  refreshTrigger = 0,
}) => {
  const journeyId = useJourneyId(journeyIdProp);
  const { authFetch } = useApiClient();
  const [prismData, setPrismData] = useState<PrismData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsGeneration, setNeedsGeneration] = useState(false);
  const [hoveredStation, setHoveredStation] = useState<StationId | null>(null);
  const [hoveredDriver, setHoveredDriver] = useState<string | null>(null);
  const [_selectedNode, _setSelectedNode] = useState<{ type: 'station' | 'driver'; id: string } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // SVG dimensions
  const SVG_WIDTH = 900;
  const SVG_HEIGHT = 480;
  const CENTER_X = SVG_WIDTH / 2;
  const CENTER_Y = SVG_HEIGHT / 2;

  // Layout helpers
  const getStationY = useCallback((index: number, total: number) => {
    const padding = 60;
    const availableHeight = SVG_HEIGHT - padding * 2;
    return padding + (index * availableHeight) / Math.max(total - 1, 1);
  }, [SVG_HEIGHT]);

  const getDriverY = useCallback((index: number, total: number) => {
    const padding = 60;
    const availableHeight = SVG_HEIGHT - padding * 2;
    return padding + (index * availableHeight) / Math.max(total - 1, 1);
  }, [SVG_HEIGHT]);

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  useEffect(() => {
    const fetchPrismData = async () => {
      setLoading(true);
      setError(null);
      setNeedsGeneration(false);

      try {
        console.log('[Prism] Fetching fresh data from source APIs...');

        // DC-871: Added lineage API call for provenance tracking
        const [rawRecsRes, bridgeRes, actionPlanRes, orgRecsRes, lineageRes] = await Promise.all([
          authFetch(`/api/journey/${journeyId}/recommendations`),
          authFetch(`/api/bridge/${journeyId}/intelligence?check_only=true`),
          authFetch(`/api/journeys/${journeyId}/action-plan`),
          authFetch('/api/organization/recommendations/for-bridge'),
          authFetch(`/api/lineage/${journeyId}/summary`),
        ]);

        if (bridgeRes.status === 404) {
          console.log('[Prism] No Bridge AI insights yet - user needs to generate');
          setNeedsGeneration(true);
          setPrismData(null);
          return;
        }

        if (!rawRecsRes.ok) {
          throw new Error(`Recommendations API error (${rawRecsRes.status})`);
        }
        if (!actionPlanRes.ok && actionPlanRes.status !== 404) {
          const errorText = await actionPlanRes.text();
          throw new Error(`Action Plan API error (${actionPlanRes.status}): ${errorText.slice(0, 100)}`);
        }

        const rawRecsData = await rawRecsRes.json();
        const actionPlanData = await actionPlanRes.json();
        const bridgeData = bridgeRes.ok ? await bridgeRes.json() : null;
        const orgRecsData = orgRecsRes.ok ? await orgRecsRes.json() : { recommendations: [] };
        const lineageData = lineageRes.ok ? await lineageRes.json() : null;

        const rawRecs = rawRecsData?.recommendations || [];
        const bridgeRecs = bridgeData?.recommendations || [];
        const tasks = actionPlanData?.action_plan?.tasks || [];
        const orgRecs = orgRecsData?.recommendations || [];

        console.log('[Prism] DATA LINEAGE:');
        console.log(`  Journey: ${rawRecs.length} findings`);
        console.log(`  Org: ${orgRecs.length} findings (documents/interviews)`);
        console.log(`  → ${bridgeRecs.length} priorities → ${tasks.length} tasks`);
        if (lineageData) {
          console.log(`  Lineage API: ${lineageData.total_events || 0} events tracked`);
        }

        const transformedData = transformToPrismData(rawRecsData, bridgeData, actionPlanData, orgRecsData, lineageData);
        setPrismData(transformedData);

        if (onDataLoaded) {
          onDataLoaded(transformedData);
        }

      } catch (err) {
        console.error('[Prism] Error fetching data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
        setPrismData(null);
      } finally {
        setLoading(false);
      }
    };

    if (journeyId) {
      fetchPrismData();
    }
  }, [journeyId, refreshTrigger, authFetch, onDataLoaded]);

  // ============================================================================
  // INTERACTION HANDLERS
  // ============================================================================

  const getConnectedDrivers = useCallback((stationId: StationId): string[] => {
    if (!prismData) return [];
    return prismData.valueDrivers
      .filter(d => d.sourceStations.includes(stationId))
      .map(d => d.id);
  }, [prismData]);

  const getConnectedStations = useCallback((driverId: string): StationId[] => {
    if (!prismData) return [];
    const driver = prismData.valueDrivers.find(d => d.id === driverId);
    return driver?.sourceStations || [];
  }, [prismData]);

  const isNodeDimmed = useCallback((type: 'station' | 'driver', id: string): boolean => {
    if (!hoveredStation && !hoveredDriver) return false;

    if (type === 'station') {
      if (hoveredStation) return id !== hoveredStation;
      if (hoveredDriver) return !getConnectedStations(hoveredDriver).includes(id as StationId);
    } else {
      if (hoveredDriver) return id !== hoveredDriver;
      if (hoveredStation) return !getConnectedDrivers(hoveredStation).includes(id);
    }
    return false;
  }, [hoveredStation, hoveredDriver, getConnectedDrivers, getConnectedStations]);

  // ============================================================================
  // RENDER
  // ============================================================================

  if (loading) {
    return (
      <div className="prism-container prism-loading">
        <div className="prism-spinner" />
        <p>Loading value provenance data...</p>
      </div>
    );
  }

  if (needsGeneration) {
    return (
      <div className="prism-container prism-awaiting">
        <div className="prism-awaiting-content">
          <div className="awaiting-icon">🔮</div>
          <h3>Awaiting AI Synthesis</h3>
          <p>
            The Intelligence Prism visualizes how station findings are synthesized into
            actionable priorities and ROI projections.
          </p>
          <p className="awaiting-instruction">
            Click <strong>"Generate Insights"</strong> above to run Bridge AI synthesis
            and reveal the value provenance flow.
          </p>
          <div className="awaiting-preview">
            <span className="preview-label">What you'll see:</span>
            <div className="preview-flow">
              <span className="flow-item">📊 Stations</span>
              <span className="flow-arrow">→</span>
              <span className="flow-item">🔮 Bridge AI</span>
              <span className="flow-arrow">→</span>
              <span className="flow-item">💰 Value Drivers</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!prismData) {
    return (
      <div className="prism-container prism-error">
        <div className="prism-error-content">
          <h3>Unable to Load Value Provenance Data</h3>
          <p>The Intelligence Prism requires completed station analyses to visualize value attribution.</p>
          {error && (
            <div className="error-detail">
              <strong>Error:</strong> {error}
            </div>
          )}
          <div className="error-actions">
            <p>To resolve this:</p>
            <ul>
              <li>Ensure at least one station analysis is complete</li>
              <li>Check that Bridge AI has generated recommendations</li>
              <li>Verify you have access to this journey</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  const { stations, valueDrivers, totals, lineage: _lineage } = prismData;

  return (
    <div className="prism-container" ref={containerRef}>
      {/* Header */}
      <div className="prism-header">
        <div className="prism-title">
          <h2>The Intelligence Prism</h2>
          <span className="prism-subtitle">Value Provenance Visualization</span>
        </div>
        <div className="prism-badge">
          <span className="badge-icon">✓</span>
          <span>Pistoia Alliance Methodology</span>
        </div>
      </div>

      {/* Summary Bar */}
      <div className="prism-summary-bar">
        <div className="summary-step">
          <span className="step-number">{stations.length}</span>
          <span className="step-label">Stations</span>
        </div>
        <div className="summary-arrow">→</div>
        <div className="summary-step">
          <span className="step-number">{totals.findings}</span>
          <span className="step-label">Findings</span>
        </div>
        <div className="summary-arrow">→</div>
        <div className="summary-step highlight">
          <span className="step-number">{totals.priorities}</span>
          <span className="step-label">Priorities</span>
        </div>
        <div className="summary-arrow">→</div>
        <div className="summary-step">
          <span className="step-number">{totals.tasks}</span>
          <span className="step-label">Tasks</span>
        </div>
        <div className="summary-arrow">→</div>
        {/*
          Was "Projected ROI" over `totals.roi` — a sum of priced task values with
          no project cost anywhere in it, so not an ROI at all; and it silently
          omitted unpriced tasks, so not a total either (S4d follow-up).
        */}
        <div className="summary-step success">
          <span className="step-number">
            {totals.valueComplete !== null
              ? `$${(totals.valueComplete / 1000).toFixed(0)}K`
              : totals.valuePricedSubtotal !== null
                ? `$${(totals.valuePricedSubtotal / 1000).toFixed(0)}K`
                : 'Unavailable'}
          </span>
          <span className="step-label">
            {totals.valueComplete !== null
              ? 'Total value'
              : totals.valuePricedSubtotal !== null
                ? 'Priced subtotal'
                : 'Total value'}
          </span>
          {totals.unpricedTasks > 0 && (
            <span className="step-note">
              {totals.unpricedTasks} task{totals.unpricedTasks === 1 ? '' : 's'} unpriced
            </span>
          )}
        </div>
      </div>

      {/* Main Visualization */}
      <div className="prism-visualization" style={{ height: SVG_HEIGHT }}>

        {/* SVG Connection Layer */}
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
          className="prism-svg-layer"
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <linearGradient id="pathGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="var(--tri-teal)" />
              <stop offset="50%" stopColor="var(--tri-teal)" />
              <stop offset="100%" stopColor="var(--tri-good)" />
            </linearGradient>
            <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="3" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Left Flows: Stations → Bridge AI */}
          {stations.map((station, i) => {
            const startY = getStationY(i, stations.length) + 20;
            const connectedDrivers = getConnectedDrivers(station.id);
            const hasConnection = connectedDrivers.length > 0;
            const isHighlighted = hoveredStation === station.id ||
              (hoveredDriver && getConnectedStations(hoveredDriver).includes(station.id));
            const isDimmed = (hoveredStation || hoveredDriver) && !isHighlighted;

            return (
              <path
                key={`link-left-${station.id}`}
                d={getSigmoidPath(170, startY, CENTER_X - 80, CENTER_Y)}
                fill="none"
                stroke={isHighlighted ? 'url(#pathGradient)' : 'var(--tri-taupe)'}
                strokeWidth={isHighlighted ? 4 : hasConnection ? 2 : 1}
                strokeOpacity={isDimmed ? 0.2 : 1}
                filter={isHighlighted ? 'url(#glow)' : undefined}
                className="prism-path prism-path-left"
              />
            );
          })}

          {/* Right Flows: Bridge AI → Value Drivers */}
          {valueDrivers.map((driver, i) => {
            const endY = getDriverY(i, valueDrivers.length) + 20;
            const isHighlighted = hoveredDriver === driver.id ||
              (hoveredStation && getConnectedDrivers(hoveredStation).includes(driver.id));
            const isDimmed = (hoveredStation || hoveredDriver) && !isHighlighted;
            const color = DRIVER_COLORS[driver.driverType].border;

            return (
              <path
                key={`link-right-${driver.id}`}
                d={getSigmoidPath(CENTER_X + 80, CENTER_Y, SVG_WIDTH - 170, endY)}
                fill="none"
                stroke={isHighlighted ? color : 'var(--tri-taupe)'}
                strokeWidth={isHighlighted ? 4 : 2}
                strokeOpacity={isDimmed ? 0.2 : 1}
                strokeDasharray={driver.priority === 'optimization' ? '6 3' : undefined}
                filter={isHighlighted ? 'url(#glow)' : undefined}
                className="prism-path prism-path-right"
              />
            );
          })}

          {/* Bridge AI "Prism" glow effect */}
          <ellipse
            cx={CENTER_X}
            cy={CENTER_Y}
            rx="90"
            ry="60"
            fill="url(#pathGradient)"
            fillOpacity="0.1"
            className="prism-glow"
          />
        </svg>

        {/* Column 1: Stations (Input) */}
        <div className="prism-column prism-column-left">
          <div className="column-header">
            <span className="column-label">INPUT</span>
            <span className="column-title">Analysis Stations</span>
          </div>
          <div className="column-nodes">
            <TooltipProvider delayDuration={200}>
              {stations.map((station, i) => {
                const isDimmed = isNodeDimmed('station', station.id);
                const isActive = hoveredStation === station.id;
                const severityColors = station.severity ? SEVERITY_COLORS[station.severity] : null;

                return (
                  <Tooltip key={station.id}>
                    <TooltipTrigger asChild>
                      <div
                        className={`prism-node prism-station ${isDimmed ? 'dimmed' : ''} ${isActive ? 'active' : ''}`}
                        style={{ top: getStationY(i, stations.length) }}
                        onMouseEnter={() => setHoveredStation(station.id)}
                        onMouseLeave={() => setHoveredStation(null)}
                        onClick={() => onStationClick?.(station.id, station.name)}
                      >
                        {severityColors && (
                          <span
                            className="severity-indicator"
                            style={{
                              backgroundColor: severityColors.bg,
                              borderColor: severityColors.border,
                            }}
                            aria-label={`Severity: ${station.severity}`}
                          />
                        )}
                        <span className="node-icon">
                          {(() => {
                            const StationIcon = station.icon;
                            return <StationIcon size={18} aria-hidden="true" />;
                          })()}
                        </span>
                        <div className="node-content">
                          <span className="node-name">{station.name}</span>
                          {station.keyMetric && (
                            <span className="node-metric">{station.keyMetric}</span>
                          )}
                          {station.source === 'org' && (
                            <span className="source-badge source-badge--org" title="Organization Context">ORG</span>
                          )}
                          {station.source === 'merged' && (
                            <span className="source-badge source-badge--merged" title="Journey + Organization">J+O</span>
                          )}
                        </div>
                        <span className="node-badge findings-badge">
                          {station.findingsCount}
                        </span>
                      </div>
                    </TooltipTrigger>
                    {(station.educational_summary || station.keyMetric) && (
                      <TooltipContent side="right" className="prism-tooltip">
                        <div className="prism-tooltip__content">
                          <div className="prism-tooltip__header">
                            <span className="prism-tooltip__icon">
                              {(() => {
                                const StationIcon = station.icon;
                                return <StationIcon size={16} aria-hidden="true" />;
                              })()}
                            </span>
                            <span className="prism-tooltip__title">{station.name}</span>
                            {station.severity && (
                              <span
                                className="prism-tooltip__severity"
                                style={{ color: SEVERITY_COLORS[station.severity].bg }}
                              >
                                {station.severity === 'success' ? '✓' :
                                 station.severity === 'warning' ? '⚠' :
                                 station.severity === 'critical' ? '✗' : 'ℹ'}
                              </span>
                            )}
                          </div>
                          {station.educational_summary && (
                            <p className="prism-tooltip__summary">{station.educational_summary}</p>
                          )}
                          {station.keyMetric && (
                            <div className="prism-tooltip__metric">
                              <span className="prism-tooltip__metric-label">Key Metric:</span>
                              <span className="prism-tooltip__metric-value">{station.keyMetric}</span>
                            </div>
                          )}
                        </div>
                      </TooltipContent>
                    )}
                  </Tooltip>
                );
              })}
            </TooltipProvider>
          </div>
        </div>

        {/* Column 2: Bridge AI Engine (Center) */}
        <div
          className="prism-engine"
          style={{ left: CENTER_X - 80, top: CENTER_Y - 60 }}
        >
          <div className="engine-label">SYNTHESIZE</div>
          <div className="engine-title">Bridge AI</div>
          <div className="engine-stats">
            <div className="engine-stat">
              <span className="stat-value">{totals.findings}</span>
              <span className="stat-arrow">→</span>
              <span className="stat-value">{totals.priorities}</span>
            </div>
            <span className="stat-label">Impact vs Effort</span>
          </div>
          <div className="engine-badge">
            <span>Anthropic Claude</span>
          </div>
        </div>

        {/* Column 3: Value Drivers (Output) */}
        <div className="prism-column prism-column-right">
          <div className="column-header">
            <span className="column-label">OUTPUT</span>
            <span className="column-title">Value Drivers</span>
          </div>
          <div className="column-nodes">
            {valueDrivers.map((driver, i) => {
              const isDimmed = isNodeDimmed('driver', driver.id);
              const isActive = hoveredDriver === driver.id;
              const colors = DRIVER_COLORS[driver.driverType];
              const isGov = isGovernanceDriver(driver.name);

              return (
                <div
                  key={driver.id}
                  className={`prism-node prism-driver ${isDimmed ? 'dimmed' : ''} ${isActive ? 'active' : ''} ${isGov ? 'governance-driver' : ''}`}
                  style={{
                    top: getDriverY(i, valueDrivers.length),
                    backgroundColor: colors.bg,
                    borderColor: colors.border,
                  }}
                  onMouseEnter={() => setHoveredDriver(driver.id)}
                  onMouseLeave={() => setHoveredDriver(null)}
                  onClick={() => onDriverClick?.(driver.id)}
                >
                  {isGov && (
                    <span className="governance-badge" title="Governance Driver">
                      ⚖️
                    </span>
                  )}
                  <div className="node-content">
                    <span className="node-name" style={{ color: colors.text }}>
                      {driver.name}
                    </span>
                    <span className="node-tasks">{driver.taskCount} tasks</span>
                  </div>
                  <span className="node-value" style={{ color: colors.text }}>
                    ${(driver.value / 1000).toFixed(0)}K
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Footer - Pistoia L0 Value Drivers Legend */}
      <div className="prism-footer">
        <div className="footer-legend">
          <span className="legend-item">
            <span className="legend-color" style={{ background: DRIVER_COLORS.COST.border }} />
            Cost
          </span>
          <span className="legend-item">
            <span className="legend-color" style={{ background: DRIVER_COLORS.SPEED.border }} />
            Speed
          </span>
          <span className="legend-item">
            <span className="legend-color" style={{ background: DRIVER_COLORS.QUALITY.border }} />
            Quality
          </span>
          <span className="legend-item">
            <span className="legend-color" style={{ background: DRIVER_COLORS.EFFECTIVENESS.border }} />
            Effectiveness
          </span>
        </div>
        {/*
          "Total Projected Value" over the same partial sum. A total that omits the
          unpriced tasks is a subtotal, and saying so is the whole point.
        */}
        <div className="footer-total">
          {totals.valueComplete !== null ? (
            <>
              <span className="total-label">Total value</span>
              <span className="total-value">${totals.valueComplete.toLocaleString()}</span>
            </>
          ) : (
            <>
              <span className="total-label">Total value unavailable</span>
              <span className="total-value">
                {totals.valuePricedSubtotal !== null
                  ? `$${totals.valuePricedSubtotal.toLocaleString()} priced subtotal`
                  : 'no task priced'}
                {totals.unpricedTasks > 0
                  ? ` · ${totals.unpricedTasks} task${totals.unpricedTasks === 1 ? '' : 's'} unpriced`
                  : ''}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default IntelligencePrism;
